/**
 * NEXORAX DIGITAL OS - HYBRID MONTHLY / YEARLY BILLING CORE
 * Version: 2026.05.24
 * Status: DUAL_PRICING_MATRIX_ARMED
 * * DIRECTION: Operates purely behind the glass layer.
 * Manages calculations for Node 50 and Node 51 commercial contracts.
 */

export class NexoraxDualBillingEngine {
  private infoEmail = "info@nexoraxdigital.co.uk";
  private supportEmail = "support@nexoraxdigital.co.uk";
  
  // Core Levy Rates
  private CHARITY_RATE = 0.01; // 1% Mandatory Zakat
  private GAVEL_RATE = 0.20;   // 20% Lifetime 10% Maintenance & Hosting Fee
  private VAT_RATE = 0.20;     // 20% UK VAT on Top

  /**
   * Generates a binding contract based on the chosen billing interval
   * @param {string} clientName - Target corporation name
   * @param {number} annualBasePrice - The standard yearly contract price (e.g., 250000 or 1000000)
   * @param {string} interval - "MONTHLY" | "YEARLY"
   */
  calculateContractManifest(clientName: string, annualBasePrice: number, interval: "MONTHLY" | "YEARLY") {
    let basePriceToProcess = 0;
    
    if (interval.toUpperCase() === "MONTHLY") {
      // Divide annual contract cost into a clean 12-month cycle
      basePriceToProcess = annualBasePrice / 12;
    } else {
      // Apply an automated upfront corporate discount for yearly commitments
      basePriceToProcess = annualBasePrice * 0.90; // 10% deduction for immediate upfront settlement
    }

    const charitySplit = basePriceToProcess * this.CHARITY_RATE;
    const gavelSplit = basePriceToProcess * this.GAVEL_RATE;
    const subtotal = basePriceToProcess + charitySplit + gavelSplit;
    const vatAmount = subtotal * this.VAT_RATE;
    const grandTotal = subtotal + vatAmount;

    const billingRecord = {
      invoiceId: `NEX-BILL-${interval.toUpperCase()}-${Date.now()}`,
      timestamp: new Date().toISOString(),
      client: clientName,
      billingInterval: interval.toUpperCase(),
      corporateContact: this.infoEmail,
      statement: {
        rawBaseFee: basePriceToProcess.toFixed(2),
        zakatDiverted: charitySplit.toFixed(2),
        gavelFeeSettlement: gavelSplit.toFixed(2),
        ukVatAmount: vatAmount.toFixed(2),
        enforcedTotalDue: grandTotal.toFixed(2)
      },
      systemStatus: "PENDING_MANDATE_SWEEP"
    };

    this.saveBillingLogToDrive(billingRecord);
    return billingRecord;
  }

  /**
   * Google Drive Automated Storage Routine (Mocked for browser environment)
   */
  private saveBillingLogToDrive(logData: any) {
    try {
      const folderTitle = "NexoraX_Hybrid_Billing_Registry";
      // In a real GAS environment, we would use DriveApp. 
      // Here we simulate the log output.
      console.log(`[BILLING NODE LOCK] Targeting drive folder: ${folderTitle}`);
      console.log(`[BILLING NODE LOCK] Outputting LEDGER_ENTRY_${logData.invoiceId}.json`);
      console.log(`[BILLING NODE LOCK] Transaction schema successfully synchronized and backed up to Drive (Simulated).`);
    } catch (err) {
      console.error("[CRITICAL SYSTEM ERROR] Billing data serialization failed: ", err);
    }
  }
}

// INITIALIZE THE MASTER ENGINE
export const BillingMatrix = new NexoraxDualBillingEngine();
